import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionCompleted, Select, Store } from "@ngxs/store";
import { Navigate } from "@ngxs/router-plugin";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";

import { PermitStep } from "@ha/data/basic";
import { PermitPaths } from "../../../permit-paths";
import { PermitRequestCandidate } from "../../../shared/models/permit-request-candidate.model";
import { PermitUserActions } from "../../../shared/states/permit-user.action";
import { PermitUserSelectors } from "../../../shared/states/permit-user.selectors";
import { PermitQuestionActions } from "../../states/permit-question.actions";
import { PermitQuestionSelectors } from "../../states/permit-question.selectors";
import { PermitCreateActions } from "../../states/permit-create.actions";
import { PermitCreateSelectors } from "../../states/permit-create.selectors";
import { PermitStepActions } from "../../../permit/states/permit-step.action";
import { PermitStepSelectors } from "../../../permit/states/permit-step.selectors";

@UntilDestroy()
@Component({
    selector: "pmt-permit-request-flammable-atex",
    templateUrl: "./permit-request-flammable-atex.component.html",
    styleUrls: ["./permit-request-flammable-atex.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitRequestFlammableAtexComponent implements OnInit {
    @Select(PermitUserSelectors.permitRequestCandidates)
    public candidates$!: Observable<PermitRequestCandidate[]>;

    @Select(PermitUserSelectors.loadingPermitRequestCandidates)
    public loadingCandidates$!: Observable<boolean | undefined>;

    @Select(PermitUserSelectors.selectedPermitRequestCandidate)
    public selectedCandidate$!: Observable<PermitRequestCandidate | undefined>;

    @Select(PermitUserSelectors.requestingPermit)
    public requestingPermit$!: Observable<boolean | undefined>;

    @Select(PermitUserSelectors.permitRequestErrorMessage)
    public errorMessage$!: Observable<string | undefined>;

    @Select(PermitUserSelectors.permitRequestSubmitted)
    public permitRequestSubmitted$!: Observable<boolean | undefined>;

    @Select(PermitQuestionSelectors.loadingQuestion)
    public loadingQuestion$!: Observable<boolean | undefined>;

    public permitId: number;
    public continueFlow = false;
    public hasCheckedRequestStatus = false;
    public hasPendingPermitRequest = false;
    public permitRequestRecipientName?: string;

    constructor(
        private store: Store,
        private route: ActivatedRoute,
        private actions$: Actions,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        const permitIdParam = this.route.snapshot.paramMap.get("permitId") ?? "";
        this.permitId = parseInt(permitIdParam, 10);
        this.continueFlow = this.route.snapshot.queryParamMap.get("continueFlow") === "true";

        if (isNaN(this.permitId) || this.permitId <= 0) {
            this.store.dispatch(new Navigate([PermitPaths.Root]));
            return;
        }

        this.store.dispatch(new PermitCreateActions.GetPermit(this.permitId)).pipe(
            take(1),
            untilDestroyed(this),
        ).subscribe(() => {
            this.updatePendingRequestState();

            if (!this.hasPendingPermitRequest) {
                this.store.dispatch(new PermitUserActions.ResetPermitRequestFlow());
                this.store.dispatch(new PermitUserActions.GetPermitRequestCandidates(this.permitId));
            }

            this.cdr.markForCheck();
        });

        this.actions$.pipe(
            ofActionCompleted(PermitUserActions.RequestPermit),
            untilDestroyed(this),
        ).subscribe(() => {
            const submitted = this.store.selectSnapshot(PermitUserSelectors.permitRequestSubmitted) === true;
            if (submitted) {
                this.store.dispatch(new PermitCreateActions.GetPermit(this.permitId)).pipe(
                    take(1),
                    untilDestroyed(this),
                ).subscribe(() => {
                    this.updatePendingRequestState();
                    this.cdr.markForCheck();
                });
                this.store.dispatch(new PermitUserActions.GetPermitRequests());

                if (this.continueFlow) {
                    this.store.dispatch(new PermitQuestionActions.GetNextQuestion());
                }
            }
        });
    }

    public toggleCandidate(candidate: PermitRequestCandidate): void {
        this.store.dispatch(new PermitUserActions.SelectPermitRequestCandidate(candidate.personId));
    }

    public requestPermit(): void {
        this.permitRequestRecipientName = this.store.selectSnapshot(
            PermitUserSelectors.selectedPermitRequestCandidate,
        )?.fullName;
        this.store.dispatch(new PermitUserActions.RequestPermit(this.permitId));
    }

    public confirmRequestSent(): void {
        if (this.continueFlow) {
            if (this.store.selectSnapshot(PermitQuestionSelectors.loadingQuestion) === true) {
                return;
            }

            if (this.store.selectSnapshot(PermitStepSelectors.currentStep)?.step === PermitStep.flammableAtexQuestion) {
                this.actions$.pipe(
                    ofActionCompleted(PermitStepActions.GoForward),
                    untilDestroyed(this),
                    take(1),
                ).subscribe(() => {
                    this.store.dispatch(new Navigate([PermitPaths.Root, this.permitId.toString()]));
                });

                this.store.dispatch(new PermitStepActions.GoForward(this.permitId));
                return;
            }

            this.store.dispatch(new Navigate([PermitPaths.Root, this.permitId.toString()]));
            return;
        }

        this.store.dispatch(new Navigate([PermitPaths.Root, this.permitId.toString()]));
    }

    public cancel(): void {
        this.store.dispatch(new Navigate([PermitPaths.Root, this.permitId.toString()]));
    }

    private updatePendingRequestState(): void {
        this.hasPendingPermitRequest = this.store.selectSnapshot(
            PermitCreateSelectors.hasPendingFlammableAtexPermitRequest,
        ) === true;
        this.permitRequestRecipientName = this.store.selectSnapshot(
            PermitCreateSelectors.pendingFlammableAtexPermitRequestRecipientName,
        ) ?? this.permitRequestRecipientName;
        this.hasCheckedRequestStatus = true;
    }
}
